import { Action, Location, State, To } from '../../types';
import { createEntryKey, createKey } from '../../utils';

import { getHistoryStateAndUrl, getNextLocation } from './shared';
import { HistoryBackend, Notify } from './types';

const globalHistory = window.history;

/**
 * Default backend: navigation lives in the real pathname via the History API.
 * Registers the popstate listener and seeds history.state with a stable key on
 * creation, so the first getLocation() returns a real key instead of 'default'.
 * Created lazily on first use — and never created in the embedded SDK (which
 * installs the hash backend up front) — so merely importing navigation-core no
 * longer writes (and clobbers) the host page's history.state.
 */
export function createBrowserBackend(notify: Notify): HistoryBackend {
    const onPop = () => notify('POP');

    window.addEventListener('popstate', onPop);
    // The seed wipes the state, but the ENTRY survives (reload / back into the app) — keep its identity.
    globalHistory.replaceState({ state: {}, key: createKey(), entryKey: globalHistory.state?.entryKey ?? createEntryKey() }, '');

    return {
        kind: 'browser',

        push(to: To, state: State = {}): void {
            const [historyState, url] = getHistoryStateAndUrl(getNextLocation(to, state));
            globalHistory.pushState(historyState, '', url);
            notify('PUSH');
        },

        replace(to: To, state: State = {}, action: Action = 'REPLACE'): void {
            // Same entry, new content: carry the entry identity (heals to fresh when absent).
            const [historyState, url] = getHistoryStateAndUrl(getNextLocation(to, state, globalHistory.state?.entryKey));
            globalHistory.replaceState(historyState, '', url);
            notify(action);
        },

        getLocation(): Location {
            const { pathname, search, hash, href } = window.location;
            const historyState = globalHistory.state || {};
            return {
                pathname,
                search,
                hash,
                href,
                state: historyState.state || null,
                key: historyState.key || 'default',
                entryKey: historyState.entryKey,
            };
        },

        go(delta: number): void {
            globalHistory.go(delta);
        },

        dispose(): void {
            window.removeEventListener('popstate', onPop);
        },
    };
}
